import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useTheme } from "../context/ThemeContext";
import { Search, User, LogOut, Shield, Menu, X, ChevronDown, Settings, Sun, Moon, Wallet, Bell } from "lucide-react";
import api from "../lib/api";

const NAV = [
  { to: "/", label: "Home", id: "home" },
  { to: "/news", label: "News", id: "news" },
  { to: "/projects", label: "New Projects", id: "projects" },
  { to: "/community", label: "Community", id: "community" },
];

const MARKETS_LINKS = [
  { to: "/coins", label: "Live Prices", desc: "100 coins with real-time data" },
  { to: "/market", label: "Market Overview", desc: "BTC dominance, fear & greed" },
];

function MediaCryptoLogo() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" fill="#facc15"/>
      <polyline points="4,22 10,12 16,18 22,8 28,14" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <circle cx="28" cy="14" r="2" fill="black"/>
    </svg>
  );
}

function MarketsDropdown() {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="flex items-center gap-1 text-sm font-medium tracking-wide uppercase transition-colors text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
        Markets <ChevronDown className={"w-3 h-3 transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div className="absolute top-full left-0 pt-2 w-56 z-50">
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)]">
            {MARKETS_LINKS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="block px-4 py-3 hover:bg-[var(--bg-elevated)] transition group"
                onClick={() => setOpen(false)}
              >
                <div className="text-sm font-medium text-[var(--text-primary)] group-hover:text-yellow-400 transition">{item.label}</div>
                <div className="text-xs text-[var(--text-tertiary)] mt-0.5">{item.desc}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsDropdown() {
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-9 h-9 border border-[var(--border-soft)] flex items-center justify-center text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400 transition"
      >
        <Settings className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-[var(--bg-panel)] border border-[var(--border-soft)] z-50 p-2">
          <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] px-2 py-1 mb-1">
            Appearance
          </div>
          <button
            onClick={toggleTheme}
            className="w-full flex items-center justify-between px-3 py-2 hover:bg-[var(--bg-elevated)] transition rounded-sm"
          >
            <span className="text-sm text-[var(--text-secondary)] flex items-center gap-2">
              {theme === "dark" ? <Moon className="w-4 h-4 text-yellow-400" /> : <Sun className="w-4 h-4 text-yellow-400" />}
              {theme === "dark" ? "Dark Mode" : "Light Mode"}
            </span>
            <div className={"w-10 h-5 rounded-full transition-colors relative " + (theme === "light" ? "bg-yellow-400" : "bg-zinc-700")}>
              <div className={"absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform " + (theme === "light" ? "translate-x-5" : "translate-x-0.5")} />
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

function NotificationBell() {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setUnread(data.unread || 0);
      setNotifs(data.notifications || []);
    } catch (_) {}
  };

  useEffect(() => {
    if (!user) return;
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const openNotifs = async () => {
    setOpen(!open);
    if (unread > 0) {
      await api.post("/notifications/read").catch(() => {});
      setUnread(0);
      setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    }
  };

  if (!user) return null;

  const EMOJIS = { like: "👍", dislike: "👎", laugh: "😂", rocket: "🚀", fire: "🔥", comment: "💬" };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={openNotifs}
        className="w-9 h-9 border border-[var(--border-soft)] flex items-center justify-center text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400 transition relative"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 text-black text-[10px] font-bold rounded-full flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-[var(--bg-panel)] border border-[var(--border-soft)] z-50 max-h-96 overflow-y-auto">
          <div className="px-4 py-3 border-b border-[var(--border-soft)] text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
            Notifications
          </div>
          {notifs.length === 0 ? (
            <div className="px-4 py-6 text-center text-[var(--text-tertiary)] text-sm">No notifications yet</div>
          ) : (
            notifs.map((n) => (
              <div key={n.id} className={"px-4 py-3 border-b border-[var(--border-soft)] hover:bg-[var(--bg-elevated)] transition " + (!n.read ? "bg-yellow-400/5" : "")}>
                <div className="text-sm text-[var(--text-secondary)]">
                  <span className="mr-1">{EMOJIS[n.reaction || n.type] || "🔔"}</span>
                  <span className="text-[var(--text-primary)] font-medium">{n.from_name}</span>
                  {n.type === "comment" ? " commented on " : " reacted to "}
                  <span className="text-yellow-400">"{n.post_title?.slice(0, 30)}{n.post_title?.length > 30 ? "..." : ""}"</span>
                </div>
                <div className="text-xs font-mono text-[var(--text-tertiary)] mt-1">
                  {new Date(n.created_at).toLocaleDateString()}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [q, setQ] = React.useState("");
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate("/news?q=" + encodeURIComponent(q.trim()));
      setMobileOpen(false);
    }
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 border-b border-[var(--border-soft)] backdrop-blur-xl"
style={{ background: "var(--bg-base)" }}
        data-testid="main-navbar"
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-10 h-16 flex items-center justify-between gap-4">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="nav-logo" onClick={closeMobile}>
            <MediaCryptoLogo />
            <span className="font-display font-bold text-lg md:text-xl tracking-tight text-[var(--text-primary)]">
              Media<span className="text-yellow-400">Crypto</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6">
            {NAV.map((n) => (
              <NavLink
                key={n.id}
                to={n.to}
                end={n.to === "/"}
                data-testid={"nav-" + n.id + "-link"}
                className={({ isActive }) =>
                  "text-sm font-medium tracking-wide uppercase transition-colors " +
                  (isActive ? "text-yellow-400" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]")
                }
              >
                {n.label}
              </NavLink>
            ))}
            <MarketsDropdown />
          </nav>

          {/* Desktop Right */}
          <div className="hidden lg:flex items-center gap-3">
            <NotificationBell />
            <SettingsDropdown />
            <form onSubmit={submitSearch} className="flex items-center bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 h-9 focus-within:border-yellow-400">
              <Search className="w-4 h-4 text-[var(--text-tertiary)] mr-2 flex-shrink-0" />
              <input
                data-testid="nav-search-input"
                type="text"
                placeholder="Search..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="bg-transparent outline-none text-sm w-32 placeholder:text-[var(--text-tertiary)] text-[var(--text-primary)]"
              />
            </form>

            {user ? (
              <div className="flex items-center gap-2">
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    data-testid="nav-admin-link"
                    className="flex items-center gap-1 text-xs font-mono uppercase tracking-wide text-yellow-400 border border-yellow-400/30 px-3 py-2 hover:bg-yellow-400/10 transition"
                  >
                    <Shield className="w-3 h-3" /> Admin
                  </Link>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="nav-user-menu-trigger"
                      className="w-9 h-9 bg-yellow-400 text-black font-bold flex items-center justify-center hover:bg-yellow-500 transition"
                    >
                      {(user.name || user.email)[0].toUpperCase()}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[var(--bg-panel)] border-[var(--border-soft)] text-[var(--text-primary)] rounded-none w-56 z-[60]">
                    <DropdownMenuLabel className="font-mono text-xs text-[var(--text-tertiary)]">
                      {user.email}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-[var(--border-soft)]" />
                    <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="nav-profile-link" className="cursor-pointer">
                      <User className="w-4 h-4 mr-2" /> Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/portfolio")} className="cursor-pointer">
                      <Wallet className="w-4 h-4 mr-2" /> Portfolio
                    </DropdownMenuItem>
                    {user.role === "admin" && (
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-admin-link-dd" className="cursor-pointer text-yellow-400">
                        <Shield className="w-4 h-4 mr-2" /> Admin Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-[var(--border-soft)]" />
                    <DropdownMenuItem onClick={logout} data-testid="nav-logout-button" className="cursor-pointer text-red-400">
                      <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" data-testid="nav-login-link">
                  <Button variant="ghost" className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)] rounded-none">
                    Sign in
                  </Button>
                </Link>
                <Link to="/register" data-testid="nav-register-link">
                  <Button className="bg-yellow-400 text-black hover:bg-yellow-500 rounded-none font-bold uppercase tracking-wide">
                    Join
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Right */}
          <div className="flex lg:hidden items-center gap-2">
            {user ? (
              <button
                className="w-9 h-9 bg-yellow-400 text-black font-bold flex items-center justify-center hover:bg-yellow-500 transition"
                onClick={() => { navigate("/profile"); closeMobile(); }}
              >
                {(user.name || user.email)[0].toUpperCase()}
              </button>
            ) : (
              <Link to="/register" onClick={closeMobile}>
                <Button className="bg-yellow-400 text-black hover:bg-yellow-500 rounded-none font-bold uppercase tracking-wide text-xs px-3 h-8">
                  Join
                </Button>
              </Link>
            )}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-9 h-9 border border-[var(--border-soft)] flex items-center justify-center text-[var(--text-primary)] hover:border-yellow-400 hover:text-yellow-400 transition"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

        </div>
      </header>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 pt-16 flex flex-col lg:hidden overflow-y-auto" style={{ background: "var(--bg-base)" }}>
          <div className="flex flex-col px-6 py-6 space-y-1">

            {NAV.map((n) => (
              <NavLink
                key={n.id}
                to={n.to}
                end={n.to === "/"}
                onClick={closeMobile}
                className={({ isActive }) =>
                  "block py-4 text-lg font-display font-bold uppercase tracking-wide border-b border-[var(--border-soft)] transition " +
                  (isActive ? "text-yellow-400" : "text-[var(--text-primary)] hover:text-yellow-400")
                }
              >
                {n.label}
              </NavLink>
            ))}

            <div className="border-b border-[var(--border-soft)]">
              <div className="py-4 text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)]">Markets</div>
              {MARKETS_LINKS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeMobile}
                  className="block py-3 pl-4 text-base font-display font-bold uppercase tracking-wide text-[var(--text-primary)] hover:text-yellow-400 transition"
                >
                  {item.label}
                  <span className="block text-xs font-mono font-normal text-[var(--text-tertiary)] normal-case tracking-normal mt-0.5">{item.desc}</span>
                </Link>
              ))}
            </div>

            {user?.role === "admin" && (
              <Link
                to="/admin"
                onClick={closeMobile}
                className="block py-4 text-lg font-display font-bold uppercase tracking-wide border-b border-[var(--border-soft)] text-yellow-400"
              >
                Admin
              </Link>
            )}

            <form onSubmit={submitSearch} className="flex items-center bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 h-11 mt-4 focus-within:border-yellow-400">
              <Search className="w-4 h-4 text-[var(--text-tertiary)] mr-2 flex-shrink-0" />
              <input
                type="text"
                placeholder="Search articles..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="bg-transparent outline-none text-sm flex-1 placeholder:text-[var(--text-tertiary)] text-[var(--text-primary)]"
              />
            </form>

            {user && (
              <div className="border-b border-[var(--border-soft)] py-4">
                <NotificationBell />
              </div>
            )}

            {/* Mobile Theme Toggle */}
<div className="border-b border-[var(--border-soft)] py-4 flex items-center justify-between">
  <span className="text-sm font-display font-bold uppercase tracking-wide text-[var(--text-primary)]">
    {theme === "dark" ? "Dark Mode" : "Light Mode"}
  </span>
  <button
    onClick={toggleTheme}
    className={"w-10 h-5 rounded-full transition-colors relative " + (theme === "light" ? "bg-yellow-400" : "bg-zinc-700")}
  >
    <div className={"absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform " + (theme === "light" ? "translate-x-5" : "translate-x-0.5")} />
  </button>
</div>

            <div className="pt-6 space-y-3">
              {user ? (
  <>
    <Link
      to="/profile"
      onClick={closeMobile}
      className="flex items-center gap-3 py-3 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition border-b border-[var(--border-soft)]"
    >
      <User className="w-5 h-5" /> Profile
    </Link>
    <Link
      to="/portfolio"
      onClick={closeMobile}
      className="flex items-center gap-3 py-3 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition border-b border-[var(--border-soft)]"
    >
      <Wallet className="w-5 h-5" /> Portfolio
    </Link>
    <button
      onClick={() => { logout(); closeMobile(); }}
      className="flex items-center gap-3 py-3 text-red-400 hover:text-red-300 transition w-full"
    >
      <LogOut className="w-5 h-5" /> Sign out
    </button>
  </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={closeMobile}
                    className="block w-full text-center border border-[var(--border-soft)] py-3 text-[var(--text-primary)] font-bold uppercase tracking-wide hover:border-yellow-400 hover:text-yellow-400 transition"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    onClick={closeMobile}
                    className="block w-full text-center bg-yellow-400 text-black py-3 font-bold uppercase tracking-wide hover:bg-yellow-500 transition"
                  >
                    Join Free
                  </Link>
                </>
              )}
            </div>

          </div>
        </div>
      )}
    </>
  );
}